import { toast } from "sonner";
import { BaseInterface, PaginatedResponse } from "../interfaces/base.interface";
import { AppManager } from "./AppManager";
import { ApiException } from "./exceptions/ApiException";
import Logger from "@/core/Logger";

export interface QueryParams {
  [key: string]: string | number | boolean | string[];
}

export class BaseService<T, K, F = QueryParams>
  implements BaseInterface<T, K, F>
{
  protected readonly apiUrl: string;
  protected path: string = "";
  protected skipOrganizationPatterns: string[] = [
    "organizations",
    "organizations/:id",
    "organizations/:id/members/:id",
    "organizations/:id/members/:id/transfer_ownership",
    "users/:id/organizations",
    "users/:id/invites",
    "invites",
    "invites/:id/accept",
    "invites/:id",
    "organizations/:id/invites",
    "users/:id/invites",
    "users",
    "users/me/",
    "users/:id",
    "subscriptions",
    "marketing_emails",
  ];

  protected skipUserPatterns: string[] = ["plans", "marketing_emails"];

  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    this.apiUrl = `${baseUrl}`;
  }

  private matchesPattern(path: string, pattern: string): boolean {
    const regexPattern = pattern
      .replace(/:[a-zA-Z]+/g, "([^/]+)")
      .replace(/\//g, "\\/");
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }

  private shouldSkipOrganization(path: string): boolean {
    return this.skipOrganizationPatterns.some((pattern) =>
      this.matchesPattern(path, pattern)
    );
  }

  private shouldSkipUser(path: string): boolean {
    return this.skipUserPatterns.some((pattern) =>
      this.matchesPattern(path, pattern)
    );
  }

  private async getAuthHeaders(path: string): Promise<HeadersInit> {
    const shouldSkipUserValidation = this.shouldSkipUser(path);
    // Inicializar headers com Content-Type sempre presente
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (!shouldSkipUserValidation) {
      const token = AppManager.getInstance().getToken();
      if (!token) {
        throw new ApiException("Usuário não autenticado", 401);
      }

      // Adicionar apenas o token de autorização
      headers["Authorization"] = `Bearer ${token}`;
    }

    const organizationId = AppManager.getInstance().getActualOrganization();
    if (organizationId && !this.shouldSkipOrganization(path)) {
      headers["x-organization"] = organizationId;
    }

    return headers;
  }

  private buildUrl(path: string, queryParams?: F): string {
    let fullPath = path;
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => params.append(key, val));
        } else {
          params.append(key, String(value));
        }
      });
      fullPath = `${path}?${params.toString()}`;
    }
    return fullPath;
  }

  private showToast(message: string, type: "success" | "error" = "success") {
    toast[type](message, {
      style: {
        backgroundColor: type === "success" ? "green" : "red",
        color: "white",
      },
    });
  }

  protected async request<R = K | K[]>(
    path: string,
    options: RequestInit & {
      queryParams?: F;
      returnFullResponse?: boolean;
    } = {}
  ): Promise<R> {
    const headers = await this.getAuthHeaders(path);
    const { queryParams, returnFullResponse = false, ...restOptions } = options;
    const fullPath = this.buildUrl(path, queryParams);

    try {
      const response = await fetch(`${this.apiUrl}/${fullPath}`, {
        ...restOptions,
        headers: { ...headers, ...(restOptions.headers || {}) },
      });

      if (!response.ok) {
        const errorDetails = await this.parseResponseBody(response);
        throw new ApiException(
          errorDetails?.message || "Erro inesperado ao realizar a requisição.",
          response.status,
          errorDetails
        );
      }

      if (["POST", "PUT"].includes(restOptions.method ?? "")) {
        this.showToast("Enviado com sucesso!");
      }

      const responseBody = await this.parseResponseBody(response);
      return returnFullResponse
        ? responseBody
        : ((responseBody?.data ?? responseBody) as R);
    } catch (error) {
      if (error instanceof ApiException) {
        this.showToast(`Erro: ${error.message}`, "error");
        Logger.error(`Erro na requisição para ${fullPath}: ${error.message}`, {
          error,
        });
        throw error;
      } else {
        this.showToast(
          "Ocorreu um erro desconhecido. Tente novamente.",
          "error"
        );
        throw new ApiException("Erro desconhecido", 500);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async parseResponseBody(response: Response): Promise<any> {
    const contentType = response.headers.get("Content-Type");
    if (!contentType?.includes("application/json")) {
      return null;
    }

    const text = await response.text();
    if (text.length === 0) {
      return null;
    }

    return JSON.parse(text);
  }

  public async findAll(
    queryParams?: F,
    options: RequestInit = {}
  ): Promise<PaginatedResponse<K>> {
    const response = await this.request<PaginatedResponse<K>>(this.path, {
      ...options,
      queryParams,
      returnFullResponse: true,
      method: "GET",
    });

    if (!response) {
      return {
        data: [],
        pagination: {
          total: 0,
          pageSize: 0,
          pages: 0,
          page: 0,
          links: {
            self: "",
            first: "",
            last: "",
            prev: "",
            next: "",
          },
        },
      };
    }
    return response;
  }

  public async findById(
    id: string,
    queryParams?: F,
    options: RequestInit = {}
  ): Promise<K> {
    return this.request(`${this.path}/${id}`, {
      ...options,
      queryParams,
      method: "GET",
    }) as Promise<K>;
  }

  public async create<U = K, D = T>(
    data: D,
    queryParams?: F,
    options?: RequestInit
  ): Promise<U> {
    return this.request(this.path, {
      ...options,
      queryParams,
      method: "POST",
      body: JSON.stringify(data),
    }) as Promise<U>;
  }

  public async update(
    id: string,
    data: Partial<T>,
    queryParams?: F,
    options: RequestInit = {}
  ): Promise<K> {
    return this.request(`${this.path}/${id}`, {
      ...options,
      queryParams,
      method: "PUT",
      body: JSON.stringify(data),
    }) as Promise<K>;
  }

  public async delete(
    id: string,
    queryParams?: F,
    options: RequestInit = {}
  ): Promise<void> {
    await this.request(`${this.path}/${id}`, {
      ...options,
      queryParams,
      method: "DELETE",
    });
  }

  // Add new method for FormData uploads
  public async createFormData<U = K, D extends FormData = FormData>(
    data: D,
    path: string = this.path,
    queryParams?: F,
    options?: RequestInit
  ): Promise<U> {
    // Get auth headers without considering Content-Type
    const headers: HeadersInit = {};

    const token = AppManager.getInstance().getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const organizationId = AppManager.getInstance().getActualOrganization();
    if (organizationId && !this.shouldSkipOrganization(path)) {
      headers["x-organization"] = organizationId;
    }

    const fullPath = this.buildUrl(path, queryParams);

    try {
      const response = await fetch(`${this.apiUrl}/${fullPath}`, {
        ...options,
        method: "POST",
        headers: { ...headers, ...(options?.headers || {}) },
        body: data,
      });

      if (!response.ok) {
        const errorDetails = await this.parseResponseBody(response);
        throw new ApiException(
          errorDetails?.message || "Erro ao realizar upload.",
          response.status,
          errorDetails
        );
      }

      this.showToast("Enviado com sucesso!");

      const responseBody = await this.parseResponseBody(response);
      return (responseBody?.data ? responseBody.data : responseBody) as U;
    } catch (error) {
      if (error instanceof ApiException) {
        this.showToast(`Erro: ${error.message}`, "error");
        Logger.error(`Erro no upload para ${fullPath}: ${error.message}`, {
          error,
        });
        throw error;
      } else {
        this.showToast(
          "Ocorreu um erro desconhecido no upload. Tente novamente.",
          "error"
        );
        throw new ApiException("Erro desconhecido no upload", 500);
      }
    }
  }
}
